import { checkFeature } from '@bybeam/platform-lib/features/check';
import { FeatureName, PaymentMethod, TaxFormType } from '@bybeam/platform-types';
import useFulfillment from '@platform-ui-common/hooks/contexts/useFulfillment.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useProgress from '@platform-ui-common/hooks/useProgress/useProgress.js';
import {
  getCompletedSteps,
  getProgressPercentage,
} from '@platform-ui-common/hooks/useProgress/utils.js';
import useTaxForms from '@platform-ui-common/hooks/useTaxForms.js';
import { getPaymentRails } from '@platform-ui-common/utilities/paymentMethods.js';
import UserHomeRedirect from '@platform-ui/auth/components/UserHomeRedirect.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ClaimContext, ClaimPayload } from './useClaim.js';
import { CLAIM_STEPS, INTRO_STEPS, TAX_FORM_STEPS } from './utils/claimSteps.js';
import { getNextPage } from './utils/getNextPage.js';

export default function ClaimProvider({ children }: { children: ReactNode }): JSX.Element {
  const { taxFormWasSubmitted } = useTaxForms();
  const { setProgress, currentStep, progress, setOptions } = useProgress();
  const [method, setMethod] = useState<Exclude<PaymentMethod, PaymentMethod.External>>();
  const program = useProgram();
  const fulfillment = useFulfillment();

  const [payload, setPayload] = useState<Partial<ClaimPayload>>();

  // There should be at least one payment rail available iF claimFund feature is enabled
  const paymentRails = getPaymentRails(program);
  const isClaimFundEnabled = checkFeature(program?.features, FeatureName.PaymentsClaimFunds);

  const steps = useMemo(() => {
    if (!method) return [];
    return [
      ...INTRO_STEPS,
      ...(checkFeature(program?.features, FeatureName.TaxFormsW9Collection) &&
      !taxFormWasSubmitted(TaxFormType.W9)
        ? TAX_FORM_STEPS
        : []),
      ...CLAIM_STEPS[method],
    ];
  }, [method, program, taxFormWasSubmitted]);

  useEffect(() => {
    if (method) setPayload(null);
  }, [method]);

  useEffect(() => {
    if (method) {
      const getClaimRoute = (route: string): string =>
        makeRoute(route, { fulfillmentId: fulfillment.id });
      setOptions({
        label: 'Section Overview:',
        disableSkip: true,
        getRoute: getClaimRoute,
      });
      setProgress({
        steps: getCompletedSteps(steps, currentStep),
        percentage: getProgressPercentage(steps, currentStep),
      });
    }
  }, [method, setProgress, currentStep, setOptions, fulfillment.id, steps]);

  useEffect(() => {
    if (progress?.steps && currentStep < 0) setProgress(null);
  }, [setProgress, currentStep, progress?.steps]);

  const value = useMemo(
    () => ({
      fulfillmentId: fulfillment.id,
      fulfillment,
      setMethod,
      method,
      setPayload,
      payload,
      paymentRails,
      getNextPage,
    }),
    [fulfillment, method, payload, paymentRails],
  );

  if (!isClaimFundEnabled) return <UserHomeRedirect />;
  return <ClaimContext.Provider value={value}>{children}</ClaimContext.Provider>;
}
